import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import Loader from "@/components/Loader";
import usePageTitle from "@/hooks/usePageTitle";
import { SPEAKING } from "@/constants/testIds";

const TOPIC_ICONS = [
  "fa-chess-king",
  "fa-shield-halved",
  "fa-bolt",
  "fa-compass",
  "fa-seedling",
];

export default function Speaking() {
  const [profile, setProfile] = useState(null);
  usePageTitle("Speaking", "Book Shri Nitin Narang for keynotes, panels and interviews — chess reform, AI governance, building Bharat's media, brand & political strategy, and the next-decade founder thesis.");

  useEffect(() => { client.get("/profile").then(r => setProfile(r.data)); }, []);

  if (!profile) return <Loader label="Loading speaking topics" />;
  const topics = profile.expertise_topics || [];

  return (
    <main data-testid={SPEAKING.page} className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
      {/* HERO */}
      <div className="grid lg:grid-cols-12 gap-12 mb-20">
        <div className="lg:col-span-8">
          <div className="eyebrow mb-4">Speaking & Keynotes</div>
          <h1 className="serif-title text-5xl lg:text-6xl text-[#0a1628] mb-6">
            On stages where <span className="italic text-[#b8975d]">institutions, founders and policy</span> meet.
          </h1>
          <p className="text-[#4a4a4a] text-[18px] leading-[1.8] max-w-2xl">
            Shri Nitin Narang accepts a limited number of speaking invitations each year — keynotes, fireside chats, university lectures and on-record interviews. He speaks at the intersection of sports administration, entrepreneurship and the future of governance in India.
          </p>
        </div>
        <aside className="lg:col-span-4 border hairline bg-[#0a1628] text-[#faf8f3] p-7 lg:p-8 self-start">
          <div className="eyebrow text-[#b8975d] mb-3">Speaker bureau enquiries</div>
          <p className="text-[14px] text-[#cfc6b3] leading-relaxed mb-6">
            For event briefs, dates, fees and format options, please use the Speaking enquiry form. The office responds within 3–5 working days.
          </p>
          <Link to="/contact" data-testid={SPEAKING.bookCta} className="inline-flex items-center gap-3 bg-[#b8975d] text-[#0a1628] px-5 py-3 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-[#cfa970] transition-colors">
            Book a Talk <i className="fa-solid fa-arrow-right text-[10px]"></i>
          </Link>
          <div className="mt-7 pt-6 border-t border-[rgba(255,255,255,0.15)] space-y-3 text-[13px]">
            <div className="flex items-center gap-3"><i className="fa-solid fa-envelope text-[#b8975d] w-4"></i><a href="mailto:nitin@theideazfactory.com" className="text-[#cfc6b3] hover:text-[#faf8f3]">nitin@theideazfactory.com</a></div>
            <div className="flex items-center gap-3"><i className="fa-brands fa-whatsapp text-[#b8975d] w-4"></i><a href="https://wa.me/919988884393" className="text-[#cfc6b3] hover:text-[#faf8f3]">+91 99888 84393</a></div>
          </div>
        </aside>
      </div>

      <div className="rule mb-16"></div>

      {/* TOPICS */}
      <section className="mb-20">
        <div className="eyebrow mb-4">Speaking topics</div>
        <h2 className="serif-title text-4xl lg:text-5xl text-[#0a1628] mb-12">Five conversations he is asked to lead.</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {topics.map((t, i) => (
            <div key={i} data-testid={SPEAKING.topic(i)} className="border hairline bg-white p-8 card-hover flex gap-5 items-start">
              <div className="w-12 h-12 flex-shrink-0 border hairline flex items-center justify-center text-[#b8975d] bg-[#faf8f3]">
                <i className={`fa-solid ${TOPIC_ICONS[i % TOPIC_ICONS.length]}`}></i>
              </div>
              <div>
                <div className="eyebrow text-[#b8975d] mb-2 text-[10px]">Topic {String(i + 1).padStart(2, '0')}</div>
                <h3 className="font-display text-2xl text-[#0a1628] leading-snug mb-3">{t.title}</h3>
                <p className="text-[14px] text-[#4a4a4a] leading-relaxed">{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FORMATS */}
      <section className="mb-20">
        <div className="eyebrow mb-4">Formats</div>
        <h2 className="serif-title text-3xl lg:text-4xl text-[#0a1628] mb-10">What works on stage.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { f: "Keynote", body: "20–40 minute keynote with case studies from AICF, Future Shift Labs and the venture portfolio." },
            { f: "Fireside chat", body: "On-stage conversation with a host or a fellow founder / administrator, with Q&A." },
            { f: "Panel discussion", body: "Anchor or participant on sports administration, AI policy, media and founder topics." },
            { f: "Interview / Podcast", body: "On-record interview for newspapers, business publications, podcasts and YouTube shows." },
          ].map((x, i) => (
            <div key={i} className="border hairline p-6 bg-white">
              <div className="font-display text-xl text-[#0a1628] mb-3">{x.f}</div>
              <p className="text-[13px] text-[#4a4a4a] leading-relaxed">{x.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SPEAKER INTRO */}
      <section className="border hairline bg-white p-8 lg:p-12">
        <div className="eyebrow mb-4">Speaker introduction · approved</div>
        <p className="font-display text-2xl text-[#0a1628] italic leading-relaxed max-w-3xl mb-6">
          “{profile.speaker_intro}”
        </p>
        <Link to="/media-kit" className="link-underline text-[12px] tracking-[0.18em] uppercase font-medium">
          Full press resources & bios →
        </Link>
      </section>
    </main>
  );
}
